import React from "react";
import { useTranslation } from "react-i18next";
import useSummaryData from "../hooks/useSummaryData";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import useReport from "../hooks/useReport";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../components/loadingContainer";
import ContainerHeader from "../components/containerHeader";
import SummaryAccordion, { AccordionRow } from "../components/summaryAccordion";
import useEcomodClasses from "../hooks/useEcomodClasses";
import { useUIStore } from "../stores/ui-store";
import { useShallow } from "zustand/shallow";
import { UIState } from "../types/ui-types";
import useSupClasses from "../hooks/useSupClasses";
import usePrices from "../hooks/usePrices";
import useReports from "../hooks/useReports";
import {
  createSummaryData,
  getMatchingReport,
  getReportingCycle,
} from "../utils/summaryUtils";
import { ReportStatus } from "../api/models/report.models";
import TitleComponent from "../components/title";
import { Report } from "../api/models/report.models";

const PaddedContainer = styled.div`
  padding: 3rem 3rem;
  text-align: left;
  flex-grow: 1;
`;

const ReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  width: 100%;
`;

const Description = styled.p`
  font-size: 0.9rem;
  max-width: 700px;
  padding: 1rem 2rem 0 4rem;
  text-align: left;
  color: var(--text-color-secondary);
`;

const CommentContainer = styled.div`
  margin-top: 2rem;
  padding: 1rem 2rem;
  background-color: var(--background-color-light);
  font-size: 0.95rem;
  color: var(--text-color-primary);

  & span {
    font-weight: 600;
    margin-right: 0.5rem;
  }
`;

const TotalCostContainer = styled.div`
  margin-top: 2rem;
  padding-right: 1rem;
  font-size: 18px;
  font-weight: 600;
  width: 100%;
  text-align: right;
  display: flex;
  flex-direction: column;

  & .notification {
    font-size: 14px;
    color: var(--text-color-secondary);
    font-weight: 500;
    margin-top: 0.5rem;
  }
`;

const ReportView: React.FC = () => {
  const { t } = useTranslation();
  const { reportId } = useParams();
  const [locale, companyId, organizationId, companyName, organizationName] =
    useUIStore(
      useShallow((state: UIState) => [
        state.locale,
        state.companyId,
        state.organizationId,
        state.companyName,
        state.organizationName,
      ]),
    );

  const { reportsData, isLoadingReports } = useReports({
    companyId,
    organizationId,
  });

  const { reportData, isLoadingReport } = useReport({
    reportId: parseInt(reportId as string, 10),
    disableCache: true,
  });

  // Find all reports from previous year, they might be yearly or quarterly
  const prevYearReports = reportData?.year
    ? reportsData?.filter(
        (report) =>
          report.year === reportData?.year - 1 &&
          report.reportStatus === ReportStatus.Sent &&
          report.id !== reportData.id,
      )
    : null;

  // Get previous year same quarter report or yearly report
  const prevYearReport = getMatchingReport(prevYearReports, reportData);

  const { pricesData, isLoading: isLoadingPrices } = usePrices({
    year: reportData ? reportData.year : new Date().getFullYear(),
  });

  const { ecomodClasses, isLoading: isLoadingEcomodClasses } =
    useEcomodClasses();

  const { supClasses, isLoading: isLoadingSupClasses } = useSupClasses();

  const {
    consumerData,
    prevYearConsumerData,
    businessData,
    prevYearBusinessData,
    supData,
    prevYearSupData,
    totalCost,
  } = useSummaryData({
    reportData,
    prevYearReport,
    ecomodClasses,
    supClasses,
    pricesData,
    locale,
  });

  const currentYearReportingCycle = getReportingCycle(reportData as Report);

  const prevYearReportingCycle = getReportingCycle(prevYearReport as Report);

  const isLoading =
    isLoadingReport ||
    isLoadingEcomodClasses ||
    isLoadingSupClasses ||
    isLoadingPrices ||
    isLoadingReports;

  return (
    <ReportContainer className="report-container">
      <LoadingContainer
        status={
          isLoading ? HttpRequestStatus.LOADING : HttpRequestStatus.SUCCESS
        }
      >
        <ContainerHeader title="" description="" />
        <TitleComponent
          text={
            t("header-report") +
            " " +
            reportData?.year +
            (reportData?.quarter ? " Q" + reportData?.quarter : "") +
            " - " +
            (organizationId ? organizationName : companyName)
          }
        />
        <Description>{t("header-report-view")}</Description>
        {reportData?.rows.length === 0 && (
          <PaddedContainer>
            <div style={{ paddingLeft: "2rem" }}>
              {t("text-no-data-in-report")}
            </div>
            {reportData.comment && (
              <CommentContainer>
                <span>{t("label-comment-from-empty-report")}:</span>
                {reportData.comment}
              </CommentContainer>
            )}
          </PaddedContainer>
        )}
        <PaddedContainer>
          {!!consumerData?.length &&
            consumerData.some((row) => row.combinedWeight > 0) && (
              <SummaryAccordion
                tableData={consumerData || []}
                prevYearData={prevYearConsumerData}
                prevYearReportingCycle={prevYearReportingCycle}
                currentYearReportingCycle={currentYearReportingCycle}
                header={t("label-consumer")}
              />
            )}
          {!!businessData?.length &&
            businessData.some((row) => row.combinedWeight > 0) && (
              <SummaryAccordion
                tableData={businessData || []}
                prevYearData={prevYearBusinessData}
                prevYearReportingCycle={prevYearReportingCycle}
                currentYearReportingCycle={currentYearReportingCycle}
                header={t("label-business")}
              />
            )}
          {!!supData?.length &&
            supData.some((row) => row.combinedWeight > 0) && (
              <SummaryAccordion
                tableData={supData || []}
                prevYearData={prevYearSupData}
                prevYearReportingCycle={prevYearReportingCycle}
                currentYearReportingCycle={currentYearReportingCycle}
                header={t("label-single-use-plastic")}
              />
            )}
          <TotalCostContainer>
            <div>
              {`${t("label-total-cost")}: ${totalCost} ${totalCost ? "€" : ""}`}
              {`${totalCost ? ` ${t("label-vat-amount")}` : ""}`}
            </div>
            <div className="notification">
              {t("label-total-amount-notification")}
            </div>
          </TotalCostContainer>
        </PaddedContainer>
      </LoadingContainer>
    </ReportContainer>
  );
};

export default ReportView;
